import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Typography, useTheme } from "@mui/material";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CALENDAR_CONFIG, CALENDAR_VIEWS } from "../../const";
import { EventWithModal } from "./modal/EventWithModal";
import { getCalendarStylesMonth } from "./calendarStylesMonth";
import { DayTimelineView } from "../daily/DayTimeLIneVIew";
import { useModals } from "src/context/ModalContext";
import { CALENDAR_SELECTED_INSTANT_KEY, resolveCompanyWallTime } from "../../utils/companyWallTime";
import { useGlobalSnackbar } from "src/context/SnackbarContext";

const toValidDate = (v: unknown): Date | null => {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v as any);
  return Number.isNaN(d.getTime()) ? null : d;
};

const dayKeyFromDate = (date: Date, zone: string) =>
  DateTime.fromJSDate(date, { zone }).toISODate();

const buildMonthGroupedEvents = (events: any[], zone: string): any[] => {
  const byDay = new Map<string, any[]>();

  for (const event of events ?? []) {
    const start = toValidDate(event?.start);
    if (!start) continue;
    const key = dayKeyFromDate(start, zone);
    const normalized = { ...event, start, end: toValidDate(event?.end) ?? undefined };
    byDay.set(key, [...(byDay.get(key) ?? []), normalized]);
  }

  return Array.from(byDay.entries()).flatMap(([day, dayEvents]) => {
    const sorted = dayEvents.sort((a, b) => a.start.getTime() - b.start.getTime());
    if (sorted.length > 2) {
      return [
        {
          id: `group-${day}`,
          title: "",
          start: day,
          allDay: true,
          classNames: ["fc-grouped-day"],
          backgroundColor: "transparent",
          borderColor: "transparent",
          textColor: "inherit",
          extendedProps: { isGroup: true, date: day, events: sorted },
        },
      ];
    }
    return sorted;
  });
};

interface CalendarContainerProps {
  eventsFromApi: any[];
  calendarRef: any;
  viewType: string;
  loading: boolean;
  isFromJob: boolean;
  currentWeek: Dayjs;
  timeZone: string;
}

export const CalendarContainer = ({
  eventsFromApi,
  calendarRef,
  viewType,
  loading,
  isFromJob,
  currentWeek,
  timeZone,
}: CalendarContainerProps) => {
  const theme = useTheme();
  const [mountKey] = useState(() => Date.now());
  const [isReady, setIsReady] = useState(false);
  const [repeatedHourChoices, setRepeatedHourChoices] = useState<Array<{ instant: string; label: string }>>([]);
  const { openModal } = useModals();
  const { showSnackbar } = useGlobalSnackbar();

  useEffect(() => {
    setIsReady(false);
    const updateSize = () => {
      const calendarApi = calendarRef.current?.getApi();
      if (calendarApi) {
        calendarApi.updateSize();
        setIsReady(true);
      } else {
        setTimeout(updateSize, 100);
      }
    };
    const timeoutId = setTimeout(updateSize, 1000);
    return () => clearTimeout(timeoutId);
  }, [calendarRef, mountKey]);

  const hasEventsInSelectedDay = useMemo(() => {
    if (!eventsFromApi) return false;
    return eventsFromApi.some((event) => dayjs(event.start).isSame(currentWeek, "day"));
  }, [eventsFromApi, currentWeek]);

  const calendarEvents = useMemo(() => {
    const events = eventsFromApi ?? [];
    if (viewType !== "dayGridMonth") return events;
    const zone = calendarRef.current?.getApi?.()?.getOption?.("timeZone") ?? "local";
    return buildMonthGroupedEvents(events, zone);
  }, [eventsFromApi, viewType, calendarRef]);

  const eventKey = calendarEvents.map((e: any) => e.id).join("-") || "empty";
  const isDayView = viewType === CALENDAR_VIEWS.DAY;
  const openCreateAtInstant = useCallback((instant: string) => {
    localStorage.setItem("jobCreateSelectedDate", instant);
    localStorage.setItem(CALENDAR_SELECTED_INSTANT_KEY, instant);
    setRepeatedHourChoices([]);
    openModal("Dashboard-JobsAdd-Business-Without-Quote-all");
  }, [openModal]);

  const handleCreateJobAtDate = useCallback(
    (date: Date | string) => {
      if (isFromJob) return;

      const localDate = typeof date === "string" ? new Date(`${date}T00:00:00`) : date;
      let resolution;
      try {
        resolution = resolveCompanyWallTime(localDate, timeZone);
      } catch {
        showSnackbar("The company time zone or selected date is invalid. Correct it before scheduling.", "error");
        return;
      }
      if (resolution.kind === "nonexistent") {
        showSnackbar(`This local time does not exist in ${timeZone}. Choose ${resolution.suggestedTime} or another valid time.`, "error");
        return;
      }
      if (resolution.kind === "ambiguous") {
        setRepeatedHourChoices(resolution.choices);
        return;
      }
      openCreateAtInstant(resolution.choices[0].instant);
    },
    [isFromJob, openCreateAtInstant, showSnackbar, timeZone]
  );

  const calendarSurface = (
    <Box
      sx={{
        position: "relative",
        opacity: isReady ? 1 : 0,
        transition: "opacity 0.3s ease-in-out",
        minHeight: "650px",
        height: "auto",
        mt: 2,
      }}
    >
      <FullCalendar
        key={`${mountKey}-${viewType}-${eventKey}`}
        ref={calendarRef}
        initialDate={currentWeek.toDate()}
        plugins={[timeGridPlugin, interactionPlugin, dayGridPlugin, listPlugin]}
        initialView={viewType}
        headerToolbar={false}
        timeZone="local"
        events={calendarEvents}
        eventOverlap={false}
        slotEventOverlap={false}
        selectable={false}
        selectMirror={true}
        editable={false}
        contentHeight={CALENDAR_CONFIG.contentHeight}
        allDaySlot={false}
        slotMinTime={CALENDAR_CONFIG.slotMinTime}
        slotMaxTime={CALENDAR_CONFIG.slotMaxTime}
        slotDuration={CALENDAR_CONFIG.slotDuration}
        nowIndicator
        dayMaxEvents={CALENDAR_CONFIG.dayMaxEvents}
        eventMaxStack={CALENDAR_CONFIG.eventMaxStack}
        dateClick={(info) => {
          handleCreateJobAtDate(info.date);
        }}
        eventClassNames={(info) =>
          info.event.extendedProps?.isGroup ? ["fc-grouped-day"] : []
        }
        eventContent={(arg) => <EventWithModal arg={arg} isFromJob={isFromJob} />}
      />
    </Box>
  );

  return (
    <>
    <Box
      id="calendar-container"
      sx={
        isDayView
          ? {
            width: "100%",
            height: "100%",
            boxSizing: "border-box",
            px: 2,
            mt: 2,
          }
          : {
            ...getCalendarStylesMonth(theme, viewType),
            pl: 3,
          }
      }
    >
      {isDayView ? (
        <DayTimelineView
          events={eventsFromApi ?? []}
          currentDay={currentWeek}
          isFromJob={isFromJob}
          onCreateJobAtDate={handleCreateJobAtDate}
          timeZone={timeZone}
        />
      ) : (
        calendarSurface
      )}
    </Box>
    <Dialog open={repeatedHourChoices.length > 0} onClose={() => setRepeatedHourChoices([])}>
      <DialogTitle>Choose the exact arrival time</DialogTitle>
      <DialogContent>
        <Typography>This hour occurs twice in {timeZone}. Select the intended time.</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setRepeatedHourChoices([])}>Cancel</Button>
        {repeatedHourChoices.map(choice => (
          <Button key={choice.instant} onClick={() => openCreateAtInstant(choice.instant)}>{choice.label}</Button>
        ))}
      </DialogActions>
    </Dialog>
    </>
  );
};
